
import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FieldValues } from 'react-hook-form';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { ChevronLeftIcon, ImageIcon, BoldIcon, ItalicIcon, UnderlineIcon, LinkIcon, ListIcon, ChevronDownIcon } from '../icons';
import { TiptapEditor, PressReleasePreview } from './';
import { ImageUploadModal } from './ImageUploadModal';
import { LinkModal } from './LinkModal';
import ImageUploadField from '../admin/ImageUploadField';
import type { z } from 'zod';

export interface HighlightOption {
  key: string;
  label: string;
  defaultValue?: boolean;
}

export interface ArticleEditorProps<T extends z.ZodType> {
  // Schema and validation
  formSchema: T;

  // Data loading
  articleId?: number | string;
  articleData?: Record<string, unknown>;
  isLoading?: boolean;

  // Callbacks
  onBack?: () => void;
  onSubmit: (data: z.infer<T>, status: 'DRAFT' | 'PUBLISH') => void;

  // UI customization
  highlightOptions: HighlightOption[];

  // State
  isSaving?: boolean;
}

export function ArticleEditor<T extends z.ZodType>({
  formSchema,
  articleId,
  articleData,
  isLoading,
  onBack,
  onSubmit,
  highlightOptions,
  isSaving = false
}: ArticleEditorProps<T>) {
  const [editorView, setEditorView] = useState<'editor' | 'publish'>('editor');
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<FieldValues>({
    // @ts-expect-error - Generic type resolution issue with zodResolver, will fix later
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      content: '',
      coverImage: '',
      coverImageTitle: '',
      articleTag: '',
      ...highlightOptions.reduce((acc, opt) => ({ ...acc, [opt.key]: opt.defaultValue || false }), {})
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline'
        }
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg'
        }
      })
    ],
    content: watch('content') || '',
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none min-h-[400px] p-3'
      }
    },
    onUpdate: ({ editor }) => {
      setValue('content', editor.getHTML());
    }
  });

  useEffect(() => {
    if (articleData) {
      // Convert articleTag to string if it's an array
      const articleTagString = Array.isArray(articleData.articleTag)
        ? articleData.articleTag.join(', ')
        : articleData.articleTag || '';

      reset({
        title: articleData.title,
        content: articleData.content,
        coverImage: articleData.coverImage,
        coverImageTitle: articleData.coverImageTitle,
        articleTag: articleTagString,
        ...highlightOptions.reduce((acc, opt) => ({
          ...acc,
          [opt.key]: articleData[opt.key] || false
        }), {})
      });
      setCoverImageUrl(articleData.coverImage as string);

      if (editor && articleData.content) {
        editor.commands.setContent(articleData.content);
      }
    }
  }, [articleData, reset, editor, highlightOptions]);

  const handleCoverImageChange = (url: string | null) => {
    if (url) {
      setCoverImageUrl(url);
      setValue('coverImage', url);
    } else {
      setCoverImageUrl('');
      setValue('coverImage', '');
    }
  };

  const handleAltTextChange = (altText: string) => {
    setValue('coverImageTitle', altText);
  };

  const setLink = useCallback(() => {
    if (!editor) return;
    setShowLinkModal(true);
  }, [editor]);

  const handleInsertLink = (url: string) => {
    if (!editor) return;

    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = useCallback(() => {
    if (!editor) return;
    setShowImageModal(true);
  }, [editor]);

  const handleInsertImage = (imageUrl: string) => {
    if (!editor) return;
    editor.chain().focus().setImage({ src: imageUrl }).run();
  };

  const handleFormSubmit = (data: FieldValues, saveStatus: 'DRAFT' | 'PUBLISH') => {
    onSubmit(data as z.infer<T>, saveStatus);
  };

  if (isLoading && articleId) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading article...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl ">
      {/* Header */}
      <header className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {/* Back Button */}
          <button
            onClick={() => onBack?.()}
            className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
          >
            <ChevronLeftIcon className="w-5 h-5" />
            Go back
          </button>

          {/* Editor Toolbar */}
          {editorView === 'editor' && editor && (
            <div className="flex items-center gap-1">
              {/* Text Style Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-semibold p-2 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    if (editor.isActive('heading', { level: 1 })) {
                      editor.chain().focus().setParagraph().run();
                    } else {
                      editor.chain().focus().toggleHeading({ level: 1 }).run();
                    }
                  }}
                >
                  {editor.isActive('heading', { level: 1 }) ? 'Heading 1' :
                   editor.isActive('heading', { level: 2 }) ? 'Heading 2' :
                   editor.isActive('heading', { level: 3 }) ? 'Heading 3' :
                   'Text Style'}
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
              </div>

              <div className="h-6 border-l border-gray-300 mx-1"></div>

              {/* Bold */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${
                  editor.isActive('bold') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
                title="Bold"
              >
                <BoldIcon className="w-5 h-5" />
              </button>

              {/* Italic */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${
                  editor.isActive('italic') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
                title="Italic"
              >
                <ItalicIcon className="w-5 h-5" />
              </button>

              {/* Underline */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${
                  editor.isActive('underline') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
                title="Underline"
              >
                <UnderlineIcon className="w-5 h-5" />
              </button>

              <div className="h-6 border-l border-gray-300 mx-1"></div>

              {/* Link */}
              <button
                type="button"
                onClick={setLink}
                className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${
                  editor.isActive('link') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
                title="Add Link"
              >
                <LinkIcon className="w-5 h-5" />
              </button>

              {/* Image */}
              <button
                type="button"
                onClick={addImage}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-600 transition-colors"
                title="Add Image"
              >
                <ImageIcon className="w-5 h-5" />
              </button>

              <div className="h-6 border-l border-gray-300 mx-1"></div>

              {/* Bullet List */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${
                  editor.isActive('bulletList') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
                title="Bullet List"
              >
                <ListIcon className="w-5 h-5" />
              </button>

              {/* Numbered List */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${
                  editor.isActive('orderedList') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
                title="Numbered List"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h12M9 12h12M9 19h12M3 5.5v3M3 12h1M3 19h1M3 8.5h1" />
                </svg>
              </button>
            </div>
          )}

          {/* Action Buttons */}
          {editorView === 'editor' ? (
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowPreview(true)}
                type="button"
                className="text-sm font-medium text-forestGreen-100 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50"
              >
                Preview
              </button>
              <button
                onClick={() => setEditorView('publish')}
                type="button"
                className="text-sm font-medium text-white bg-forestGreen-100 rounded-lg px-4 py-2 hover:bg-gray-500"
              >
                Publish
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <button
                onClick={() => setEditorView('editor')}
                className="text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50"
              >
                Back to Editor
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Editor View */}
      {editorView === 'editor' ? (
        <div className="p-6 md:p-10 max-w-3xl mx-auto">
          {/* Title */}
          <textarea
            {...register('title')}
            placeholder="Title"
            className="text-3xl md:text-4xl font-bold text-gray-800 w-full resize-none border-none outline-none focus:outline-none focus:ring-0 focus:border-none p-0 mb-6 h-auto"
            rows={2}
          />
          {errors.title && <p className="text-red-500 text-sm mb-4">{(errors.title as any).message}</p>}

          {/* Cover Image */}
          <div className="mb-6">
            <ImageUploadField
              label="Cover Image"
              value={coverImageUrl}
              preview={coverImageUrl}
              onChange={handleCoverImageChange}
              onAltTextChange={handleAltTextChange}
              required={true}
            />
          </div>

          {/* Content Editor */}
          <div className="mb-6">
            <TiptapEditor editor={editor} />
            {errors.content && <p className="text-red-500 text-sm mt-1">{(errors.content as any).message}</p>}
          </div>
        </div>
      ) : (
        /* Publish View */
        <div className="p-6 md:p-10 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Publish</h2>

          {/* Preview Card */}
          <div className="mb-6">
            {coverImageUrl ? (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex gap-4">
                  <img
                    src={coverImageUrl}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-lg flex-shrink-0 bg-gray-100"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">
                      {watch('title') || "Article Title"}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {watch('content')?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/gi, '').trim().substring(0, 200) || "Brief description..."}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex gap-4">
                  <div className="w-32 h-32 rounded-lg flex-shrink-0 bg-gray-100 flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">
                      {watch('title') || "Article Title"}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {watch('content')?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/gi, '').trim().substring(0, 200) || "Brief description..."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Article Tags */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Article Tags</label>
            <input
              type="text"
              {...register('articleTag')}
              placeholder="Enter tags separated by commas (e.g., Space, Technology, Innovation)"
              className="text-base text-gray-700 w-full bg-gray-50 rounded-md border-none outline-none focus:outline-none focus:ring-0 p-3"
            />
            <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
            {errors.articleTag && <p className="text-red-500 text-sm mt-1">{(errors.articleTag as any).message}</p>}
          </div>

          {/* Highlight Options */}
          <div className="mb-6 bg-gray-50 p-3 rounded-md">
            <label className="block text-base font-semibold text-gray-700 mb-3">Set Highlight</label>
            <div className="space-y-2">
              {highlightOptions.map((option) => (
                <label key={option.key} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...register(option.key)}
                    className="w-4 h-4 text-wine-100 border-gray-300 focus:ring-0 accent-wine-100"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Allow Comments Toggle - check if allowComment or allowComments exists in highlight options */}
          {(highlightOptions.some(opt => opt.key === 'allowComment' || opt.key === 'allowComments')) && (() => {
            const commentField = highlightOptions.find(opt => opt.key === 'allowComment' || opt.key === 'allowComments');
            return commentField ? (
              <div className="flex items-center justify-between mb-6">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Allow comments?</label>
                  <p className="text-xs text-gray-500">This disables the comment section of this content</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    {...register(commentField.key)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-wine-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-wine-100"></div>
                </label>
              </div>
            ) : null;
          })()}

          {/* Submit Buttons */}
          <div className="flex items-center justify-end gap-4">
            <button
              onClick={handleSubmit((data) => handleFormSubmit(data, 'DRAFT'))}
              disabled={isSaving}
              className="text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg px-6 py-2 hover:bg-gray-50 disabled:opacity-50"
            >
              Save Draft
            </button>
            <button 
              onClick={handleSubmit((data) => handleFormSubmit(data, 'PUBLISH'))}
              disabled={isSaving}
              className="text-sm font-medium text-white bg-forestGreen-100 rounded-lg px-6 py-2 hover:bg-green-700 disabled:opacity-50"
            >
              Upload
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onInsert={handleInsertImage}
      />
      <LinkModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onInsert={handleInsertLink}
      />
      <PressReleasePreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title={watch('title') || ''}
        coverImage={coverImageUrl}
        content={watch('content') || ''}
      />
    </div>
  );
}

export default ArticleEditor;
